import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { ArrowRightIcon } from 'assets/icons';
import { CustomButton, ErrorScreenWithRetry, Loader, NoPermissions } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import {
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectCurrentTeamRefundPermissions
} from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import routesPath from 'routes/RoutesPath';
import { OutletContextType } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsDisputes } from '../events';
import { useGetDisputeVisibilityQuery } from '../api';
import { DisputeListPreset } from '../disputes.model';
import { DisputeChevron } from './components/DisputeZoneChip';
import { DisputeTaggedText } from './components/DisputeTaggedText';
import DisputeFunnelSection from './components/DisputeFunnelSection';
import DisputeOverviewShell from './components/DisputeOverviewShell';
import DisputeRateSection from './components/DisputeRateSection';
import DisputeWaysToReduce from './components/DisputeWaysToReduce';
import { buildAllDisputesPreset, buildReceiptsListPreset, normalizeDisputeVisibility, windowRange } from './utils';

const isForbidden = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const status = 'status' in error ? error.status : undefined;
  const data = 'data' in error ? (error.data as { code?: number } | undefined) : undefined;
  return status === 403 || data?.code === 403;
};

const DisputeOverview: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSidePanelClose } = useOutletContext<OutletContextType>();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { viewDisputes } = useAppSelector(selectCurrentTeamRefundPermissions);
  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const canView =
    Boolean(viewDisputes) &&
    (Boolean(invoicePermissions.viewReceiptsBySelf) || Boolean(invoicePermissions.viewReceiptsByTeam));

  const { data, isLoading, isError, error, refetch } = useGetDisputeVisibilityQuery(currentTeam?.id ?? 0, {
    skip: !currentTeam?.id || !canView
  });
  const summary = data ? normalizeDisputeVisibility(data) : null;
  const [isWaysExpanded, setIsWaysExpanded] = useState<boolean | null>(null);

  useEffect(() => {
    if (!location.state?.shouldAnimate) return undefined;
    const timeout = setTimeout(() => {
      navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
    }, 800);
    return () => clearTimeout(timeout);
  }, [location.pathname, location.state?.shouldAnimate, navigate]);

  const handleOpenTechnicians = () => {
    const techniciansPath = location.pathname.startsWith(routesPath.PAYOUTS_BALANCE)
      ? routesPath.PAYOUTS_BALANCE_DISPUTE_TECHNICIANS
      : routesPath.DISPUTE_TECHNICIANS;
    logAnalyticEvent(CleverTapEventsDisputes.webDisputesOverviewAction, { action: 'by_technician' });
    navigate(techniciansPath, { state: { fromSidePanel: true } });
  };

  const handleViewDisputes = () => {
    logAnalyticEvent(CleverTapEventsDisputes.webDisputesOverviewAction, { action: 'view_disputes' });
    navigate(routesPath.DISPUTES);
  };

  const handleOpenDisputes = (preset: DisputeListPreset, action: string) => {
    logAnalyticEvent(CleverTapEventsDisputes.webDisputesOverviewAction, { action, tab: preset.tab });
    navigate(routesPath.DISPUTES, { state: { disputeListPreset: preset } });
  };

  const handleOpenPayments = (start?: string | null, end?: string | null, action?: string, merchantId?: string) => {
    logAnalyticEvent(CleverTapEventsDisputes.webDisputesOverviewAction, { action });
    navigate(routesPath.RECEIPTS, {
      state: { showSidePanel: false, receiptsListPreset: buildReceiptsListPreset(start, end, merchantId) }
    });
  };

  if (!canView || isForbidden(error)) {
    return (
      <DisputeOverviewShell onClose={handleSidePanelClose} shouldAnimate={location.state?.shouldAnimate}>
        <NoPermissions handleErrorBack={handleSidePanelClose} />
      </DisputeOverviewShell>
    );
  }

  if (isLoading || !currentTeam?.id) {
    return (
      <DisputeOverviewShell onClose={handleSidePanelClose} shouldAnimate={location.state?.shouldAnimate}>
        <Loader />
      </DisputeOverviewShell>
    );
  }

  if (isError || !summary) {
    return (
      <DisputeOverviewShell onClose={handleSidePanelClose} shouldAnimate={location.state?.shouldAnimate}>
        <ErrorScreenWithRetry handleReload={refetch} />
      </DisputeOverviewShell>
    );
  }

  const waysExpanded = isWaysExpanded ?? summary.reduceDisputesOpen;

  return (
    <DisputeOverviewShell onClose={handleSidePanelClose} shouldAnimate={location.state?.shouldAnimate}>
      <div className="flex flex-col pb-6">
        <DisputeRateSection
          summary={summary}
          onPayments={(disputeWindow, action) => {
            const range = windowRange(disputeWindow);
            handleOpenPayments(range.start, range.end, action);
          }}
          onDisputed={(disputeWindow, action) => {
            const range = windowRange(disputeWindow);
            handleOpenDisputes(buildAllDisputesPreset(range.start, range.end), action);
          }}
        />
        <div className="mt-8">
          <DisputeFunnelSection summary={summary} onOpenList={handleOpenDisputes} />
        </div>
        {summary.techniciansVisible ? (
          <button
            type="button"
            id="dispute-overview-technicians"
            className="mt-4 w-full rounded-md border border-borderGray p-4 text-left"
            onClick={handleOpenTechnicians}
            aria-label="Disputes by technician">
            <div className="flex items-center gap-3">
              <span className="flex-1 text-sbase font-semibold text-heading">Disputes by technician</span>
              <DisputeChevron />
            </div>
            {summary.concentration ? (
              <p className="mt-1 text-px13 text-accentText">
                <DisputeTaggedText text={summary.concentration} />
              </p>
            ) : null}
          </button>
        ) : null}
        {summary.tips.length ? (
          <div className="mt-8">
            <DisputeWaysToReduce
              tips={summary.tips}
              expanded={waysExpanded}
              onToggle={() => setIsWaysExpanded(!waysExpanded)}
            />
          </div>
        ) : null}
        <CustomButton
          id="dispute-overview-view-disputes"
          type={ButtonType.SECONDARY}
          className="mt-6 h-10 w-full"
          onClick={handleViewDisputes}
          EndIcon={ArrowRightIcon}
          endIconStyle="h-2.5">
          View disputes
        </CustomButton>
        {summary.footer ? <p className="mt-6 text-xs text-accentText">{summary.footer}</p> : null}
      </div>
    </DisputeOverviewShell>
  );
};

export default DisputeOverview;
